/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { classNames } from '../utils/UNSAFE_classNames';
import { LegendSymbol } from './LegendSymbol';
import { LegendText } from './LegendText';
import { SymbolProps, TextStylesProps } from './Legend.type';
import { ForwardedRef, forwardRef } from 'preact/compat';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { styles } from './themes/LegendStyles.css';

type BaseLegendItemProps = {
  text: string;
  itemIdx: number;
  sectionIdx: number;
  isCurrent: boolean;
  isFocused: boolean;
  isHighlighted?: boolean;
  isHidden?: boolean;
  symbolWidth?: number;
  symbolHeight?: number;
  labelledBy?: string;
  id?: string;
  'aria-label'?: HTMLAttributesSignalExcluded['aria-label'];
  class?: string;
  minWidth?: number;
  gridCol?: number;
  gridRow?: number;
} & SymbolProps &
  TextStylesProps;

export const BaseLegendItem = forwardRef(
  (
    {
      id,
      text,
      sectionIdx,
      itemIdx,
      isHighlighted,
      symbolHeight,
      symbolWidth,
      isFocused,
      isCurrent,
      isHidden,
      ...props
    }: BaseLegendItemProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const textStyles = {
      fontFamily: props.textFontFamily,
      fontSize: props.textFontSize,
      color: props.textColor,
      fontStyle: props.textFontStyle,
      fontWeight: props.textFontWeight,
      textDecoration: props.textTextDecoration
    };

    const symbolProps = {
      lineStyle: props.lineStyle,
      lineWidth: props.lineWidth,
      markerShape: props.markerShape,
      markerColor: props.markerColor,
      source: props.source,
      borderColor: props.borderColor,
      lineColor: props.lineColor
    };

    const isNotDimmed = isHighlighted || isHighlighted == null;
    const { legendItemBaseStyle, legendItemIsCurrent, legendItemOpacity, legendItemFocusRing } =
      styles;
    return (
      <div
        class={classNames([
          legendItemBaseStyle,
          isCurrent && isHighlighted == null ? legendItemIsCurrent : '',
          !isNotDimmed ? legendItemOpacity : '',
          isFocused ? legendItemFocusRing : '',
          props.class
        ])}
        style={{
          gridRow: props.gridRow,
          gridColumn: props.gridCol,
          minWidth: props.minWidth !== undefined ? `${props.minWidth}px` : undefined
        }}
        id={id}
        ref={ref}
        data-oj-section={sectionIdx}
        data-oj-item={itemIdx}
        aria-labelledby={props.labelledBy}
        role={isHidden != null ? 'menuitemcheckbox' : 'img'}
        aria-checked={isHidden != null ? !isHidden : undefined}
        aria-label={props['aria-label'] || text}>
        <LegendSymbol
          {...symbolProps}
          isHidden={isHidden}
          width={symbolWidth != null ? `${symbolWidth}px` : undefined}
          height={symbolHeight != null ? `${symbolHeight}px` : undefined}
        />
        <LegendText {...textStyles} text={text} type="label" />
      </div>
    );
  }
);
